import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { sprintf } from "sprintf-js"

import iconAudiobook from "@images/svg/audiobook.svg"
import iconEpub from "@images/svg/epub.svg"
import Button from "@knowit-alma/common/src/components/atoms/Button/Button"
import ButtonGroup from "@knowit-alma/common/src/components/molecules/ButtonGroup/ButtonGroup"
import Card from "@knowit-alma/common/src/components/molecules/Card/Card"
import RowList from "@knowit-alma/common/src/components/molecules/RowList/RowList"

const BookCatalog = ({ books }) => {
  const { t } = useTranslation()

  const [activeCategory, setActiveCategory] = useState(null)
  const [activeType, setActiveType] = useState(null)

  const categories = Array.isArray(books)
    ? books
        .map((item, index) => {
          return item?.extra?.category?.toLowerCase()
        })
        .filter(Boolean)
    : []

  const tabs = [...new Set(categories.sort())].map((item, index) => {
    const capitalizedName = item[0].toUpperCase() + item.slice(1).toLowerCase()
    return (
      <Button
        key={item}
        variant="tab"
        state={activeCategory === item ? "active" : ""}
        onClick={() => setActiveCategory(item)}
      >
        {capitalizedName}
      </Button>
    )
  })

  const filters = [
    {
      slug: "epub",
      title: t("E-book"),
    },
    {
      slug: "audio",
      title: t("Audio book"),
    },
  ]
    .map((item, index) => {
      return (
        <Button
          key={item.slug}
          variant={item.slug === activeType ? "primary" : "secondary"}
          size="small"
          onClick={() => setActiveType(item.slug)}
        >
          {item.title}
        </Button>
      )
    })
    .filter(Boolean)

  const bookGrid = Array.isArray(books)
    ? books
        .filter((item) => {
          let flag = true
          if (
            activeCategory &&
            item?.extra?.category?.toLowerCase() !==
              activeCategory.toLowerCase()
          ) {
            flag = false
          }
          if (activeType === "audio" && !item?.has_audio) {
            flag = false
          }
          if (activeType === "epub" && !item?.has_epub) {
            flag = false
          }
          return flag
        })
        .filter(Boolean)
        .map((item, index) => {
          const { childImageSharp: thumbnail } = item?.image || {}
          const image = thumbnail
            ? {
                sourceUrl: thumbnail?.resize?.src,
                mediaDetails: {
                  width: thumbnail?.resize?.width,
                  height: thumbnail?.resize?.height,
                },
              }
            : item?.cover_url
            ? {
                sourceUrl: item.cover_url,
                mediaDetails: {
                  width: item?.cover_width,
                  height: item?.cover_height,
                },
              }
            : false

          const audiobookIcon = item?.has_audio ? (
            <img
              src={iconAudiobook}
              alt={t("This book is available as an audio book")}
              title={t("This book is available as an audio book")}
            />
          ) : (
            false
          )
          const ebookIcon = item?.has_epub ? (
            <img
              src={iconEpub}
              alt={t("This book is available as an e-book")}
              title={t("This book is available as an e-book")}
            />
          ) : (
            false
          )

          const availability =
            item?.has_audio || item?.has_epub ? (
              <span>
                {audiobookIcon} {ebookIcon}
              </span>
            ) : (
              false
            )

          return (
            <Card
              key={item.id}
              headingSize="h3"
              headingSmall
              image={image}
              imageAspectRatio={"1:1"}
              tagline={item?.author}
              taglineSmall
              title={item?.title}
              subtitle={availability}
              disableCloudinary={true}
              fullAreaIsLink={false}
            />
          )
        })
    : []

  return (
    <section className="text-align-left base-margin-top-xs base-margin-bottom">
      {tabs && tabs.length > 0 && (
        <>
          <h2 className="screen-reader-text">{t("Book category")}</h2>
          <ButtonGroup wide tabs>
            <Button
              variant="tab"
              state={!activeCategory ? "active" : ""}
              onClick={() => setActiveCategory(null)}
            >
              {t("All")}
            </Button>
            {tabs}
          </ButtonGroup>
        </>
      )}
      {filters && (
        <div className="base-margin-top base-margin-bottom">
          <h2 className="screen-reader-text">{t("Book type")}</h2>
          <ButtonGroup wide oneLine smallGutters>
            <Button
              variant={!activeType ? "primary" : "secondary"}
              onClick={() => setActiveType(null)}
              size="small"
            >
              {t("All")}
            </Button>
            {filters}
          </ButtonGroup>
        </div>
      )}
      <h2 className="screen-reader-text">{t("Books")}</h2>
      <p>
        {sprintf(
          t(
            activeType === "audio"
              ? "%s audio books found"
              : activeType === "epub"
              ? "%s e-books found"
              : "%s books found"
          ),
          bookGrid.length
        )}
        {activeCategory ? (
          <span>
            {" "}
            (
            {activeCategory[0].toUpperCase() +
              activeCategory.slice(1).toLowerCase()}
            )
          </span>
        ) : (
          ""
        )}
        .
      </p>

      <RowList
        columns={4}
        specialClass={`mobile-bottom-margins base-margin-bottom-xs`}
      >
        {bookGrid}
      </RowList>
    </section>
  )
}

export default BookCatalog
